/**
 * System-aware checks for a rules document (EPIC-0045 Phase 3).
 *
 * The schema says whether a document is well-FORMED. These say whether it is
 * COHERENT — whether the pieces refer to each other correctly and whether the
 * author has left a hole the runtime will paper over silently at play time.
 *
 * Silent papering-over is the specific thing being hunted. `creationSpecFor`
 * pads a short standard array rather than truncating, the ladder falls back
 * rather than erroring, and `getDmGuidance` synthesises a brief when none is
 * declared. Each of those is the right runtime behaviour and the wrong thing to
 * discover at a table, so each gets a warning here instead.
 *
 * Errors block publication. Warnings do not, because a system can legitimately
 * be sparse — Trophy Dark declares no combat at all and is not broken.
 */
/** Outcomes a ladder can name, in the order a reader expects them. */
const LADDER_OUTCOMES = [
    'critical-success',
    'success',
    'partial',
    'failure',
    'critical-failure',
];
export function auditGameSystemRules(rules) {
    const errors = [];
    const warnings = [];
    const abilityIds = new Set(rules.abilities.map((a) => a.id));
    const conditionIds = new Set((rules.conditions ?? []).map((c) => c.id));
    const classIds = new Set((rules.classes ?? []).map((c) => c.id));
    // --- The ladder covers what the resolution can produce ---------------------
    const ladder = rules.coreResolution?.outcomeLadder;
    if (ladder) {
        const named = new Set(ladder.rungs.map((r) => r.outcome));
        named.add(ladder.fallback.outcome);
        const degrees = rules.coreResolution?.degreesOfSuccess;
        if (degrees && named.size < degrees) {
            errors.push(`coreResolution declares ${degrees} degrees of success but the outcome ladder ` +
                `names only ${named.size} distinct outcomes (${[...named].join(', ')}). ` +
                'A degree the ladder cannot produce is a result no player can ever get.');
        }
        // A ladder with no success rung is almost always a mistake, and it is the
        // kind that reads fine: every rung is individually plausible.
        if (!named.has('success') && !named.has('critical-success')) {
            warnings.push('the outcome ladder never produces a success — every roll fails or partially succeeds. ' +
                'Intentional for some horror systems; check it is intentional for this one.');
        }
        for (const rung of ladder.rungs) {
            if (!LADDER_OUTCOMES.includes(rung.outcome)) {
                errors.push(`outcome ladder rung names an unknown outcome "${rung.outcome}"`);
            }
        }
    }
    else if (rules.coreResolution) {
        warnings.push('no outcomeLadder declared, so interpretation falls back to the built-in d20 ladder. ' +
            'Correct for a d20 system and wrong for everything else — declare one if this is not d20.');
    }
    // A variant's own ladder gets the same success check. Contests and combat
    // rolls are where "nobody can ever win this" is most likely to be a typo.
    for (const variant of rules.coreResolution?.variants ?? []) {
        const own = variant.outcomeLadder;
        if (!own)
            continue;
        const named = new Set([...own.rungs.map((r) => r.outcome), own.fallback.outcome]);
        if (!named.has('success') && !named.has('critical-success')) {
            warnings.push(`the outcome ladder of variant "${variant.id}" never produces a success. ` +
                'Check that is intended for this roll.');
        }
    }
    // --- The creation spec fits the abilities ----------------------------------
    // creationSpecFor pads rather than truncates (ISS-0154 item 2), so a short
    // array becomes extra scores in the wizard that no ability can receive.
    const creation = rules.characterTemplate
        ?.abilityCreation;
    const standardArray = creation?.standardArray;
    if (standardArray && standardArray.length !== rules.abilities.length) {
        warnings.push(`standardArray has ${standardArray.length} scores for ${rules.abilities.length} abilities. ` +
            'The wizard pads rather than truncates, so a short array shows players scores they cannot assign.');
    }
    // --- Cross-references resolve ----------------------------------------------
    for (const cls of rules.classes ?? []) {
        for (const ability of cls.primaryAbility ?? []) {
            if (!abilityIds.has(ability)) {
                errors.push(`class "${cls.id}" names primaryAbility "${ability}", which is not a declared ability`);
            }
        }
        for (const save of cls.savingThrows ?? []) {
            if (!abilityIds.has(save)) {
                errors.push(`class "${cls.id}" names savingThrow "${save}", which is not a declared ability`);
            }
        }
    }
    for (const track of rules.tracks ?? []) {
        const governing = track.governedBy;
        if (governing && !abilityIds.has(governing)) {
            errors.push(`track "${track.id}" is governedBy "${governing}", which is not a declared ability`);
        }
        // Absent harm is read from direction, which is right for Ruin and hit
        // points and wrong for a resource. The author is the one who knows which
        // this is, so they are asked (ISS-0155).
        if (track.harm === undefined) {
            warnings.push(`track "${track.id}" does not say what harm does to it, so harm will ` +
                `${track.direction === 'filling' ? 'raise' : 'lower'} it because it is ${track.direction}. ` +
                'Set harm: raises, lowers, or none (none for a resource such as coin or tokens).');
        }
    }
    const combat = rules.combat;
    for (const condition of combat?.startingConditions ?? []) {
        if (!conditionIds.has(condition)) {
            errors.push(`combat references condition "${condition}", which is not declared`);
        }
    }
    if (rules.characterTemplate?.usesClasses && classIds.size === 0) {
        errors.push('characterTemplate.usesClasses is true but no classes are declared. ' +
            'Set usesClasses: false for a classless system — the wizard has nothing to offer otherwise.');
    }
    // --- Guidance is present and says something --------------------------------
    const guidance = rules.dmGuidance;
    if (!guidance) {
        warnings.push('no dmGuidance declared. The runtime synthesises a brief from the rest of the document, ' +
            'which is a floor rather than a substitute — an empty brief is where the model fills in 5e.');
    }
    else {
        if (guidance.systemBrief.trim().length < 80) {
            warnings.push('dmGuidance.systemBrief is very short. It is the main thing standing between the AI GM ' +
                'and its own priors about how a roleplaying game works.');
        }
        if ((guidance.commonMistakes ?? []).length === 0) {
            warnings.push('dmGuidance.commonMistakes is empty. This is the highest-value field in the block: ' +
                'it is where you say what the model will otherwise get wrong by defaulting to D&D.');
        }
    }
    return { errors, warnings };
}
//# sourceMappingURL=system-audit.js.map